import {
  ActionIcon,
  Anchor,
  Avatar,
  Badge,
  Button,
  Card,
  Center,
  Drawer,
  Grid,
  Group,
  MultiSelect,
  Paper,
  Skeleton,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ART_FORM_GROUP_LABELS, ART_FORMS, type ArtFormGroup } from '@wwaave/shared/vocab';
import { useMemo, useState } from 'react';

import { AppShell } from '../../components/app-shell/AppShell';
import { JoinLink, MotionSection, PageHero } from '../../components/ui';
import { ApiError, fetchJson } from '../../lib/api';

// ─── Types (mirror worker DTOs) ────────────────────────────────────────────

type ArtistCardDTO = {
  id: string;
  slug: string;
  displayName: string;
  city: string | null;
  country: string | null;
  bio: string | null;
  disciplineSummary: string | null;
  profilePhotoUrl: string | null;
  verified: boolean;
  reviewAverage: number | null;
  reviewCount: number;
};

type ArtistSearchResult = {
  results: ArtistCardDTO[];
  next_cursor: string | null;
  total_estimate: number;
};

type ArtistBrowseSearch = {
  q?: string | undefined;
  art_form?: string[] | undefined;
  country?: string | undefined;
};

export const Route = createFileRoute('/browse/artists')({
  validateSearch: (search: Record<string, unknown>): ArtistBrowseSearch => {
    const out: ArtistBrowseSearch = {};
    if (typeof search.q === 'string' && search.q.length > 0) out.q = search.q;
    if (Array.isArray(search.art_form)) {
      out.art_form = search.art_form.filter((s): s is string => typeof s === 'string');
    } else if (typeof search.art_form === 'string') {
      out.art_form = [search.art_form];
    }
    if (typeof search.country === 'string' && search.country.length > 0) out.country = search.country;
    return out;
  },
  component: BrowseArtistsPage,
});

function buildQueryString(s: ArtistBrowseSearch, cursor: string | null | undefined): string {
  const params = new URLSearchParams();
  if (s.q) params.set('q', s.q);
  if (s.country) params.set('country', s.country);
  for (const slug of s.art_form ?? []) params.append('art_form', slug);
  if (cursor) params.set('cursor', cursor);
  return params.toString();
}

async function fetchArtists(
  s: ArtistBrowseSearch,
  cursor: string | null | undefined,
): Promise<ArtistSearchResult> {
  const qs = buildQueryString(s, cursor);
  return fetchJson<ArtistSearchResult>(`/api/browse/artists${qs ? `?${qs}` : ''}`);
}

/** True when the artist search was refused by the `public_browse` gate, not by a fault. */
function isBrowseClosed(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403;
}

/**
 * Shown when `/api/browse/artists` returns 403 — mirrors `BrowseNotOpenYet`
 * on the venue browse page (routes/browse/index.tsx) so both directories give
 * an honest "not open yet" message instead of a false "something broke" one.
 */
function BrowseArtistsNotOpenYet(): React.JSX.Element {
  return (
    <Paper p="xl" withBorder>
      <Stack gap="sm" align="center">
        <Text fw={600} size="lg">
          The artist list opens with early access.
        </Text>
        <Text size="sm" c="dimmed" ta="center" maw={460}>
          Founding artists are still being welcomed in. Join the early-access list and
          you&apos;ll be among the first through the door.
        </Text>
        <JoinLink>
          <Button
            component="span"
            tabIndex={-1}
            size="md"
            radius="md"
            variant="gradient"
            gradient={{ from: 'brand.6', to: 'sun.6', deg: 35 }}
          >
            Get Early Access
          </Button>
        </JoinLink>
      </Stack>
    </Paper>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

function BrowseArtistsPage(): React.JSX.Element {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: '/browse/artists' });
  const [filtersOpen, filtersCtl] = useDisclosure(false);
  const [qInput, setQInput] = useState(search.q ?? '');

  const query = useInfiniteQuery({
    queryKey: ['browse-artists', search],
    queryFn: ({ pageParam }: { pageParam: string | null | undefined }) =>
      fetchArtists(search, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage: ArtistSearchResult) => lastPage.next_cursor,
    placeholderData: keepPreviousData,
  });

  const cards = useMemo(
    () => query.data?.pages.flatMap((p) => p.results) ?? [],
    [query.data],
  );
  const total = query.data?.pages[0]?.total_estimate ?? 0;

  const updateSearch = (patch: Partial<ArtistBrowseSearch>): void => {
    void navigate({ search: (prev) => ({ ...prev, ...patch }) });
  };

  const onSubmitQuery = (e: React.FormEvent): void => {
    e.preventDefault();
    const trimmed = qInput.trim();
    updateSearch({ q: trimmed || undefined });
  };

  const activeFilters: { key: keyof ArtistBrowseSearch; label: string; clear: () => void }[] = [];
  if (search.art_form && search.art_form.length > 0) {
    for (const slug of search.art_form) {
      const af = ART_FORMS.find((x) => x.slug === slug);
      activeFilters.push({
        key: 'art_form',
        label: af?.name ?? slug,
        clear: () => updateSearch({ art_form: search.art_form?.filter((s) => s !== slug) }),
      });
    }
  }
  if (search.country) {
    activeFilters.push({
      key: 'country',
      label: `Country: ${search.country}`,
      clear: () => updateSearch({ country: undefined }),
    });
  }

  return (
    <AppShell tone="content">
      <Stack gap="lg">
        <MotionSection>
          <PageHero
            eyebrow="Who's on the road"
            title="Browse artists"
            lede={
              total > 0
                ? `${total} ${total === 1 ? 'artist' : 'artists'} touring and open to exchange.`
                : 'Musicians, performers, and creators looking for rooms to play.'
            }
            action={
              <Button variant="default" onClick={filtersCtl.open} radius="md">
                Narrow it down
              </Button>
            }
          />
        </MotionSection>

        {/* Search bar */}
        <form onSubmit={onSubmitQuery} style={{ maxWidth: 480 }}>
          <TextInput
            placeholder="Search by name, city, or discipline"
            value={qInput}
            onChange={(e) => setQInput(e.currentTarget.value)}
            aria-label="Search artists"
            leftSection={<SearchIcon />}
          />
        </form>

        {/* Active filter chips */}
        {activeFilters.length > 0 && (
          <Group gap="xs" wrap="wrap">
            {activeFilters.map((f, i) => (
              <Badge
                key={`${f.key}-${i}`}
                variant="light"
                size="lg"
                rightSection={
                  <ActionIcon
                    size="xs"
                    color="gray"
                    radius="xl"
                    variant="transparent"
                    onClick={f.clear}
                    aria-label={`Remove ${f.label} filter`}
                  >
                    ×
                  </ActionIcon>
                }
              >
                {f.label}
              </Badge>
            ))}
          </Group>
        )}

        {/* Results */}
        {query.isLoading ? (
          <ResultsSkeleton />
        ) : query.isError ? (
          isBrowseClosed(query.error) ? (
            <BrowseArtistsNotOpenYet />
          ) : (
            <Paper p="xl" withBorder>
              <Stack gap="xs" align="center">
                <Text>Couldn&apos;t load artists right now. Refresh and try again.</Text>
                <Button variant="default" onClick={() => void query.refetch()}>
                  Try again
                </Button>
              </Stack>
            </Paper>
          )
        ) : cards.length === 0 ? (
          <Paper p="xl" withBorder>
            <Stack gap="xs" align="center">
              <Text fw={500}>No artists match those filters yet.</Text>
              <Text size="sm" c="dimmed" ta="center">
                Try a broader search or fewer art forms. New artists join every week.
              </Text>
            </Stack>
          </Paper>
        ) : (
          <>
            <Grid gap="md">
              {cards.map((c) => (
                <Grid.Col key={c.id} span={{ base: 12, sm: 6, lg: 4 }}>
                  <ArtistCard card={c} />
                </Grid.Col>
              ))}
            </Grid>
            {query.hasNextPage && (
              <Center>
                <Button
                  variant="default"
                  onClick={() => void query.fetchNextPage()}
                  loading={query.isFetchingNextPage}
                >
                  Load more
                </Button>
              </Center>
            )}
          </>
        )}
      </Stack>

      <Drawer
        opened={filtersOpen}
        onClose={filtersCtl.close}
        position="right"
        size="md"
        title="Narrow it down"
      >
        <FilterPanel search={search} onApply={updateSearch} onClose={filtersCtl.close} />
      </Drawer>
    </AppShell>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

// Brand-only dispersion: Clay / Tide / Sun plus shade variants for variety.
const AVATAR_COLORS = ['brand', 'tide', 'sun', 'brand.8', 'tide.8', 'sun.8', 'brand.4', 'tide.4'] as const;

function avatarColor(seed: string): (typeof AVATAR_COLORS)[number] {
  const hash = seed.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
   
  return AVATAR_COLORS[hash % AVATAR_COLORS.length]!;
}

function ArtistCard({ card }: { card: ArtistCardDTO }): React.JSX.Element {
  const place = [card.city, card.country].filter(Boolean).join(', ');
  const initials = card.displayName
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

  return (
    <Link
      to="/artists/$slug"
      params={{ slug: card.slug }}
      style={{ textDecoration: 'none', color: 'inherit', height: '100%', display: 'block', cursor: 'pointer' }}
    >
      <Card
        withBorder
        padding="md"
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          '--card-hover-transform': 'translateY(-3px)',
          '--card-hover-shadow': '0 8px 24px -4px color-mix(in srgb, var(--wwaave-brand-primary) 10%, transparent), 0 4px 12px -2px rgba(233, 162, 59, 0.08)',
        } as React.CSSProperties}
        className="wwaave-card-hover"
      >
        <Group gap="md" align="flex-start" wrap="nowrap">
          <Avatar
            src={card.profilePhotoUrl}
            alt={card.displayName}
            size={64}
            radius="xl"
            color={avatarColor(card.displayName || card.id)}
          >
            {initials}
          </Avatar>
          <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
            <Group gap="xs" wrap="nowrap">
              <Title order={4} lh={1.2} style={{ wordBreak: 'break-word' }}>
                {card.displayName}
              </Title>
              {card.verified && (
                <Badge size="xs" variant="filled" color="tide">
                  Verified
                </Badge>
              )}
            </Group>
            {place && (
              <Text size="sm" c="dimmed">
                {place}
              </Text>
            )}
          </Stack>
        </Group>

        <Stack gap="xs" mt="sm" style={{ flex: 1 }}>
          {card.disciplineSummary && (
            <Text size="sm" fw={500}>
              {card.disciplineSummary}
            </Text>
          )}
          {card.bio && (
            <Text size="sm" lineClamp={2} c="dimmed">
              {card.bio}
            </Text>
          )}
          <Group gap="xs" mt="auto" wrap="wrap">
            {card.reviewAverage !== null && (
              <Text size="xs" c="dimmed">
                ★ {card.reviewAverage.toFixed(1)} ({card.reviewCount})
              </Text>
            )}
          </Group>
        </Stack>
      </Card>
    </Link>
  );
}

function ResultsSkeleton(): React.JSX.Element {
  return (
    <Grid gap="md">
      {Array.from({ length: 6 }).map((_, i) => (
        <Grid.Col key={i} span={{ base: 12, sm: 6, lg: 4 }}>
          <Card withBorder padding="md">
            <Group gap="md" wrap="nowrap">
              <Skeleton height={64} circle />
              <Stack gap="xs" style={{ flex: 1 }}>
                <Skeleton height={16} width="60%" />
                <Skeleton height={12} width="40%" />
              </Stack>
            </Group>
            <Stack gap="xs" mt="sm">
              <Skeleton height={12} width="80%" />
              <Skeleton height={12} width="60%" />
            </Stack>
          </Card>
        </Grid.Col>
      ))}
    </Grid>
  );
}

function SearchIcon(): React.JSX.Element {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

function FilterPanel({
  search,
  onApply,
  onClose,
}: {
  search: ArtistBrowseSearch;
  onApply: (patch: Partial<ArtistBrowseSearch>) => void;
  onClose: () => void;
}): React.JSX.Element {
  const [draft, setDraft] = useState<ArtistBrowseSearch>(search);

  const artFormOptions = useMemo(() => {
    const buckets = new Map<string, { value: string; label: string }[]>();
    for (const a of ART_FORMS) {
      const arr = buckets.get(a.group) ?? [];
      arr.push({ value: a.slug, label: a.name });
      buckets.set(a.group, arr);
    }
    return Array.from(buckets.entries()).map(([group, items]) => ({
      group: ART_FORM_GROUP_LABELS[group as ArtFormGroup] ?? group,
      items,
    }));
  }, []);

  const apply = (): void => {
    onApply({
      art_form: draft.art_form && draft.art_form.length > 0 ? draft.art_form : undefined,
      country: draft.country || undefined,
    });
    onClose();
  };

  const reset = (): void => {
    const cleared: ArtistBrowseSearch = { q: search.q };
    setDraft(cleared);
    onApply({ art_form: undefined, country: undefined });
    onClose();
  };

  return (
    <Stack gap="md">
      <MultiSelect
        label="Art forms"
        description="Show artists who perform in all selected forms."
        data={artFormOptions ?? []}
        value={draft.art_form ?? []}
        onChange={(v) => setDraft({ ...draft, art_form: v })}
        searchable
        clearable
      />
      <TextInput
        label="Country"
        description="Two-letter country code (e.g. AU, US, UK)."
        value={draft.country ?? ''}
        onChange={(e) => setDraft({ ...draft, country: e.currentTarget.value.toUpperCase().slice(0, 2) })}
        maxLength={2}
      />
      <Group justify="space-between" mt="md">
        <Anchor component="button" type="button" onClick={reset}>
          Reset all
        </Anchor>
        <Button onClick={apply}>Show results</Button>
      </Group>
    </Stack>
  );
}
